import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader, SkipForward } from 'lucide-react';
import {
  applyMassEnrichmentItem,
  getLatestMassEnrichmentRun,
  getMassEnrichmentRun,
  listMassEnrichmentItems,
  skipMassEnrichmentItem,
  type MassEnrichmentItem,
  type MassEnrichmentRun,
  type MatchConfidence,
} from '../../../utils/massEnrichmentApi';

const CONFIDENCE_LABELS: Record<MatchConfidence, string> = {
  high: 'Élevée',
  medium: 'Moyenne',
  low: 'Faible',
  none: 'Aucune',
};

const CONFIDENCE_STYLES: Record<MatchConfidence, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-orange-100 text-orange-800',
  none: 'bg-gray-100 text-gray-600',
};

type ConversionMode = 'usda' | 'manual';

type Draft = {
  ciqualCode: string;
  skipCiqual: boolean;
  portionId: string;
  skipConversion: boolean;
  conversionMode: ConversionMode;
  unitWeight: string;
  density: string;
};

function defaultDraft(item: MassEnrichmentItem): Draft {
  const hasUsda =
    item.conversionKind === 'unitWeight' && (item.usdaCandidates?.length || 0) > 0;
  return {
    ciqualCode: item.suggestedCiqualCode != null ? String(item.suggestedCiqualCode) : '',
    skipCiqual: !item.needsCiqual,
    portionId: item.suggestedPortionId || '',
    skipConversion: !item.needsConversion,
    conversionMode: hasUsda ? 'usda' : 'manual',
    unitWeight: '',
    density: '',
  };
}

export function MassEnrichmentReviewPage() {
  const [searchParams] = useSearchParams();
  const runIdParam = searchParams.get('runId');
  const [run, setRun] = useState<MassEnrichmentRun | null>(null);
  const [items, setItems] = useState<MassEnrichmentItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (preferredRunId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      let nextRun: MassEnrichmentRun;
      if (preferredRunId) {
        nextRun = (await getMassEnrichmentRun(preferredRunId)).run;
      } else {
        const res = await getLatestMassEnrichmentRun();
        if (!res.run) throw new Error('Aucun run Mass Enrichment disponible');
        nextRun = res.run;
      }
      setRun(nextRun);
      const itemsRes = await listMassEnrichmentItems(nextRun.id, 'pending');
      setItems(itemsRes.items);
      const nextDrafts: Record<string, Draft> = {};
      for (const item of itemsRes.items) {
        nextDrafts[item.ingredientId] = defaultDraft(item);
      }
      setDrafts(nextDrafts);
    } catch (err) {
      setError((err as Error).message);
      setRun(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(runIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runIdParam]);

  const updateDraft = (id: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const removeItem = (ingredientId: string) => {
    setItems((prev) => prev.filter((i) => i.ingredientId !== ingredientId));
  };

  const onApply = async (item: MassEnrichmentItem) => {
    if (!run) return;
    const d = drafts[item.ingredientId] || defaultDraft(item);
    setBusy(item.ingredientId);
    setError(null);
    try {
      await applyMassEnrichmentItem({
        runId: run.id,
        ingredientId: item.ingredientId,
        skipCiqual: item.needsCiqual ? d.skipCiqual : true,
        ciqualCode:
          item.needsCiqual && !d.skipCiqual && d.ciqualCode
            ? Number(d.ciqualCode)
            : null,
        skipConversion: item.needsConversion ? d.skipConversion : true,
        portionId:
          item.needsConversion &&
          !d.skipConversion &&
          item.conversionKind === 'unitWeight' &&
          d.conversionMode === 'usda' &&
          d.portionId
            ? d.portionId
            : null,
        unitWeight_g:
          item.needsConversion &&
          !d.skipConversion &&
          item.conversionKind === 'unitWeight' &&
          d.conversionMode === 'manual' &&
          d.unitWeight
            ? Number(d.unitWeight)
            : null,
        density_g_per_mL:
          item.needsConversion &&
          !d.skipConversion &&
          item.conversionKind === 'density' &&
          d.density
            ? Number(d.density)
            : null,
      });
      removeItem(item.ingredientId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onSkip = async (item: MassEnrichmentItem) => {
    if (!run) return;
    setBusy(item.ingredientId);
    setError(null);
    try {
      await skipMassEnrichmentItem(run.id, item.ingredientId);
      removeItem(item.ingredientId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
        <Loader className="h-5 w-5 animate-spin" />
        Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/mass-db/enrichment"
          className="inline-flex items-center text-sm text-gray-500 hover:text-menioo-navy mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour Enrichment
        </Link>
        <h3 className="text-lg font-semibold text-menioo-navy">Revue Mass Enrichment</h3>
        {run && (
          <p className="text-sm text-gray-500 mt-1">
            Run {run.id.slice(0, 8)}… · {items.length} restant(s)
            {run.nextOffset != null && ` · suite offset ${run.nextOffset}`}
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500">
          File vide.{" "}
          <Link to="/admin/mass-db/enrichment" className="text-menioo-green hover:underline">
            Lancer une nouvelle page
          </Link>
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map((item) => {
            const d = drafts[item.ingredientId] || defaultDraft(item);
            const isBusy = busy === item.ingredientId;
            return (
              <li
                key={item.ingredientId}
                className="rounded-lg border border-gray-200 bg-white p-4 space-y-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="font-semibold text-menioo-navy">{item.ingredientName}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {item.preferredUnit || '—'}
                      {item.nameEn ? ` · EN: ${item.nameEn}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => onSkip(item)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      <SkipForward className="h-3.5 w-3.5" />
                      Skip
                    </button>
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => onApply(item)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-menioo-green rounded-md hover:opacity-90 disabled:opacity-50"
                    >
                      {isBusy ? (
                        <Loader className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                      Appliquer
                    </button>
                  </div>
                </div>

                {item.needsCiqual && (
                  <section className="rounded-md border border-emerald-100 bg-emerald-50/40 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h5 className="text-sm font-medium text-emerald-900">CIQUAL</h5>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${CONFIDENCE_STYLES[item.ciqualConfidence]}`}
                      >
                        {CONFIDENCE_LABELS[item.ciqualConfidence]}
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={d.skipCiqual}
                        onChange={(e) =>
                          updateDraft(item.ingredientId, { skipCiqual: e.target.checked })
                        }
                      />
                      Ignorer CIQUAL pour cet item
                    </label>
                    {!d.skipCiqual && (
                      <div className="space-y-1.5">
                        {item.ciqualCandidates.length === 0 ? (
                          <p className="text-xs text-gray-500">Aucun candidat — cochez ignorer ou Skip.</p>
                        ) : (
                          item.ciqualCandidates.map((c) => (
                            <label
                              key={c.ciqualCode}
                              className={`flex items-start gap-2 text-sm rounded-md px-2 py-1.5 cursor-pointer ${
                                d.ciqualCode === String(c.ciqualCode)
                                  ? 'bg-white border border-emerald-300'
                                  : 'hover:bg-white/70'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`ciqual-${item.ingredientId}`}
                                checked={d.ciqualCode === String(c.ciqualCode)}
                                onChange={() =>
                                  updateDraft(item.ingredientId, {
                                    ciqualCode: String(c.ciqualCode),
                                  })
                                }
                              />
                              <span className="flex-1">
                                <span className="font-medium">{c.nameRaw}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  score {Math.round(c.score)}
                                  {c.kcal != null ? ` · ${c.kcal} kcal` : ''}
                                </span>
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </section>
                )}

                {item.needsConversion && (
                  <section className="rounded-md border border-sky-100 bg-sky-50/40 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h5 className="text-sm font-medium text-sky-900">
                        Conversion
                        {item.conversionKind === 'density' ? ' (densité)' : ' (poids unitaire)'}
                      </h5>
                      {item.conversionKind === 'unitWeight' && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${CONFIDENCE_STYLES[item.usdaConfidence]}`}
                        >
                          {CONFIDENCE_LABELS[item.usdaConfidence]}
                        </span>
                      )}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={d.skipConversion}
                        onChange={(e) =>
                          updateDraft(item.ingredientId, { skipConversion: e.target.checked })
                        }
                      />
                      Ignorer conversion pour cet item
                    </label>
                    {!d.skipConversion && item.conversionKind === 'unitWeight' && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-3 text-xs">
                          {item.usdaCandidates.length > 0 && (
                            <label className="inline-flex items-center gap-1.5 text-gray-700">
                              <input
                                type="radio"
                                name={`conv-mode-${item.ingredientId}`}
                                checked={d.conversionMode === 'usda'}
                                onChange={() =>
                                  updateDraft(item.ingredientId, { conversionMode: 'usda' })
                                }
                              />
                              Candidat USDA
                            </label>
                          )}
                          <label className="inline-flex items-center gap-1.5 text-gray-700">
                            <input
                              type="radio"
                              name={`conv-mode-${item.ingredientId}`}
                              checked={d.conversionMode === 'manual'}
                              onChange={() =>
                                updateDraft(item.ingredientId, {
                                  conversionMode: 'manual',
                                  portionId: '',
                                })
                              }
                            />
                            Saisie manuelle
                          </label>
                        </div>

                        {d.conversionMode === 'usda' && item.usdaCandidates.length > 0 && (
                          <div className="space-y-1.5">
                            {item.usdaCandidates.map((c) => (
                              <label
                                key={c.id}
                                className={`flex items-start gap-2 text-sm rounded-md px-2 py-1.5 cursor-pointer ${
                                  d.portionId === c.id
                                    ? 'bg-white border border-sky-300'
                                    : 'hover:bg-white/70'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`usda-${item.ingredientId}`}
                                  checked={d.portionId === c.id}
                                  onChange={() =>
                                    updateDraft(item.ingredientId, {
                                      portionId: c.id,
                                      conversionMode: 'usda',
                                    })
                                  }
                                />
                                <span className="flex-1">
                                  <span className="font-medium">{c.descriptionEn}</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    {c.modifier} · {c.unitWeight_g} g · score {Math.round(c.score)}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {d.conversionMode === 'manual' && (
                          <label className="block text-sm">
                            <span className="text-xs text-gray-600">
                              Poids unitaire (g / pièce)
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={d.unitWeight}
                              onChange={(e) =>
                                updateDraft(item.ingredientId, {
                                  unitWeight: e.target.value,
                                  conversionMode: 'manual',
                                  portionId: '',
                                })
                              }
                              className="mt-1 w-40 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                              placeholder="ex. 12"
                            />
                            {item.usdaCandidates.length === 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Aucun candidat USDA — saisissez le poids à la main.
                              </p>
                            )}
                          </label>
                        )}
                      </div>
                    )}
                    {!d.skipConversion && item.conversionKind === 'density' && (
                      <label className="block text-sm">
                        <span className="text-xs text-gray-600">Densité (g/mL)</span>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={d.density}
                          onChange={(e) =>
                            updateDraft(item.ingredientId, { density: e.target.value })
                          }
                          className="mt-1 w-40 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                          placeholder="ex. 1.03"
                        />
                      </label>
                    )}
                  </section>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
